import { useQuery, gql, TypedDocumentNode } from '@apollo/client'

import { getCacheableContext } from '@emico/apollo'
import { AttributeInput } from '@emico/graphql-schema-types'

import {
  CustomAttributeMetadataQuery,
  CustomAttributeMetadataQueryVariables,
} from './useCustomAttributeByValue.generated'

const query = gql`
  query CustomAttributeMetadata($attributes: [AttributeInput!]!) {
    customAttributeMetadata(attributes: $attributes) {
      items {
        attributeCode
        attributeOptions {
          label
          value
        }
      }
    }
  }
` as TypedDocumentNode<
  CustomAttributeMetadataQuery,
  CustomAttributeMetadataQueryVariables
>

/**
 * Get one or more attribute options
 * @param attributes One or more attribute options
 */
export function useCustomAttributes(attributes: AttributeInput[]) {
  return useQuery(query, {
    variables: { attributes },
    context: getCacheableContext(),
  })
}

/**
 * Find an attribute object by its value.
 * @param attribute Attribute set to search in
 * @param value Value of the attribute
 * @param entityType Entity type, defaults to catalog_product
 */
export default function useCustomAttributeByValue(
  attribute: string,
  value?: string,
  entityType: string = 'catalog_product',
) {
  const { data, ...rest } = useCustomAttributes([
    {
      attribute_code: attribute,
      entity_type: entityType,
    },
  ])

  // In the query above we only query 1 customAttributeMetadata,
  // so we can safely asume it's the first.
  const attributes = data?.customAttributeMetadata?.items?.[0]

  return {
    data: attributes?.attributeOptions?.find(
      (attribute) => attribute && attribute.value === value,
    ),
    ...rest,
  }
}
